import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, KeyRound } from "lucide-react";
import { API } from "./shared";

// Unified auth: existing accounts sign in at /diagnostic/login; brand-new
// invited emails set their first password right here (register), then land on
// their portfolio dashboard.

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [state, setState] = useState("checking"); // checking | nologin | register | accepting | done | error
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const gotoLogin = () => navigate(`/diagnostic/login?next=${encodeURIComponent(`/portfolio/invite/${token}`)}`, { replace: true });

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    (async () => {
      // Are we authenticated?
      const me = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      if (me.ok) {
        // Accept the invite for the logged-in account.
        setState("accepting");
        const r = await fetch(`${API}/api/portfolio/invite/${token}/accept`, {
          method: "POST", credentials: "include",
        });
        if (r.ok) {
          setState("done");
          setTimeout(() => navigate("/portfolio/dashboard"), 1400);
        } else {
          const d = await r.json().catch(() => ({}));
          setError(d.detail || "We couldn't accept this invitation.");
          setState("error");
        }
        return;
      }
      // Not logged in — does this invited email need to register (set a password)?
      const infoRes = await fetch(`${API}/api/portfolio/invite/${token}/info`, { credentials: "include" });
      if (!infoRes.ok) {
        const d = await infoRes.json().catch(() => ({}));
        setError(d.detail || "This invitation is no longer valid.");
        setState("error");
        return;
      }
      const data = await infoRes.json();
      setInfo(data);
      setState(data.needs_registration ? "register" : "nologin");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitRegister = async () => {
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/portfolio/invite/${token}/register`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw, first_name: info?.first_name || "", last_name: info?.last_name || "" }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setState("done");
        setTimeout(() => navigate("/portfolio/dashboard"), 1200);
      } else if (r.status === 409) {
        // Account already exists — send them to sign in.
        setState("nologin");
      } else {
        setError(typeof d.detail === "string" ? d.detail : "We couldn't create your account.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const Card = ({ children }) => (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div data-testid="invite-accept-card" className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">{children}</div>
    </div>
  );

  if (state === "checking" || state === "accepting") {
    return <Card><Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-400" /><p className="mt-4 text-sm text-slate-500">{state === "accepting" ? "Activating your portfolio access…" : "Loading…"}</p></Card>;
  }

  if (state === "register") {
    return (
      <Card>
        <KeyRound className="mx-auto h-10 w-10 text-[#00698a]" />
        <h1 className="mt-4 text-xl font-semibold text-slate-900">Set Your Password</h1>
        <p className="mt-2 text-sm text-slate-500">
          You've been invited to <span className="font-medium text-slate-700">{info?.portfolio_name}</span>.
          Create a password for <span className="font-medium text-slate-700">{info?.email}</span> to activate your read-only cross-school dashboard.
        </p>
        <div className="mt-6 space-y-3 text-left">
          <div>
            <label className="text-xs font-medium text-slate-500">New password</label>
            <input type="password" data-testid="invite-register-password" value={pw} onChange={(e) => setPw(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Confirm password</label>
            <input type="password" data-testid="invite-register-password-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitRegister()}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="Re-enter password" />
          </div>
          {error && <p data-testid="invite-register-error" className="text-sm text-red-600">{error}</p>}
        </div>
        <button data-testid="invite-register-submit" onClick={submitRegister} disabled={submitting}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#007a9e] px-4 py-3 text-sm font-medium text-white hover:bg-[#00607d] disabled:opacity-60">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate My Account"}
        </button>
        <button onClick={gotoLogin} className="mt-3 text-xs text-slate-400 hover:text-slate-600">Already have an account? Sign in</button>
      </Card>
    );
  }

  if (state === "nologin") {
    return (
      <Card>
        <AlertTriangle className="mx-auto h-10 w-10 text-[#00698a]" />
        <h1 className="mt-4 text-xl font-semibold text-slate-900">Accept Your Portfolio Invitation</h1>
        <p className="mt-2 text-sm text-slate-500">Sign in to MySchoolRx with the email this invitation was sent to{info?.email ? ` (${info.email})` : ""}, then you'll be returned here to activate your dashboard.</p>
        <button data-testid="invite-signin" onClick={gotoLogin} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#007a9e] px-4 py-3 text-sm font-medium text-white hover:bg-[#00607d]">
          Sign In
        </button>
      </Card>
    );
  }

  if (state === "done") {
    return <Card><CheckCircle2 className="mx-auto h-10 w-10 text-green-600" /><h1 className="mt-4 text-xl font-semibold text-slate-900">You're all set!</h1><p className="mt-2 text-sm text-slate-500">Redirecting to your portfolio dashboard…</p></Card>;
  }

  return (
    <Card>
      <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
      <h1 className="mt-4 text-xl font-semibold text-slate-900">Invitation Problem</h1>
      <p data-testid="invite-error" className="mt-2 text-sm text-slate-500">{error}</p>
      <button onClick={gotoLogin} className="mt-6 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">Sign in with a different account</button>
    </Card>
  );
}
